// Environment-aware logging utility
// Replaces console.log statements with proper logging service

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogEntry {
  level: LogLevel;
  message: string;
  data?: any;
  timestamp: string;
  source?: string;
}

class Logger {
  private isDevelopment = process.env.NODE_ENV === 'development';
  private isProduction = process.env.NODE_ENV === 'production';

  private formatMessage(level: LogLevel, message: string, data?: any, source?: string): LogEntry {
    return {
      level,
      message,
      data,
      timestamp: new Date().toISOString(),
      source: source || 'unknown'
    };
  }

  private shouldLog(level: LogLevel): boolean {
    if (this.isDevelopment) return true;
    if (this.isProduction) {
      // In production, only log warnings and errors
      return level === 'warn' || level === 'error';
    }
    return true;
  }

  private logToConsole(entry: LogEntry): void {
    const { level, message, data, timestamp, source } = entry;
    const prefix = `[${timestamp}] [${level.toUpperCase()}] [${source}]`;
    
    switch (level) {
      case 'debug':
        console.debug(prefix, message, data);
        break;
      case 'info':
        console.info(prefix, message, data);
        break;
      case 'warn':
        console.warn(prefix, message, data);
        break;
      case 'error':
        console.error(prefix, message, data);
        break;
    }
  }

  private async logToService(entry: LogEntry): Promise<void> {
    // In production, send to logging service (e.g., Sentry, LogRocket, etc.)
    if (this.isProduction && entry.level === 'error') {
      // Example: Send to error tracking service
      // await errorTrackingService.captureException(entry);
    }
  }

  debug(message: string, data?: any, source?: string): void {
    if (!this.shouldLog('debug')) return;
    
    const entry = this.formatMessage('debug', message, data, source);
    this.logToConsole(entry);
    this.logToService(entry);
  }

  info(message: string, data?: any, source?: string): void {
    if (!this.shouldLog('info')) return;
    
    const entry = this.formatMessage('info', message, data, source);
    this.logToConsole(entry);
    this.logToService(entry);
  }

  warn(message: string, data?: any, source?: string): void {
    if (!this.shouldLog('warn')) return;
    
    const entry = this.formatMessage('warn', message, data, source);
    this.logToConsole(entry);
    this.logToService(entry);
  }

  error(message: string, data?: any, source?: string): void {
    if (!this.shouldLog('error')) return;
    
    const entry = this.formatMessage('error', message, data, source);
    this.logToConsole(entry);
    this.logToService(entry);
  }

  // Convenience methods for common patterns
  apiCall(method: string, url: string, data?: any): void {
    this.debug(`API ${method} ${url}`, data, 'api');
  }

  userAction(action: string, data?: any): void {
    this.info(`User action: ${action}`, data, 'user');
  }

  performance(operation: string, duration: number, data?: any): void {
    this.info(`Performance: ${operation} took ${duration}ms`, data, 'performance');
  }

  security(event: string, data?: any): void {
    this.warn(`Security event: ${event}`, data, 'security');
  }
}

// Create singleton instance
export const logger = new Logger();

// Export convenience functions
export const log = {
  debug: (message: string, data?: any, source?: string) => logger.debug(message, data, source),
  info: (message: string, data?: any, source?: string) => logger.info(message, data, source),
  warn: (message: string, data?: any, source?: string) => logger.warn(message, data, source),
  error: (message: string, data?: any, source?: string) => logger.error(message, data, source),
  apiCall: (method: string, url: string, data?: any) => logger.apiCall(method, url, data),
  userAction: (action: string, data?: any) => logger.userAction(action, data),
  performance: (operation: string, duration: number, data?: any) => logger.performance(operation, duration, data),
  security: (event: string, data?: any) => logger.security(event, data),
};

// Export default logger instance
export default logger;











